// View-only client-side cost preview for active sessions.
// Mirrors backend `calculateBill` for hourly / package / fixed / open WITHOUT
// discounts or holiday rules. Backend remains authoritative — never use this
// value in any persisted document.

function ceilToBucket(minutes, roundingType) {
  if (roundingType === 'exact') return minutes;
  if (roundingType === 'ceil_half_hour') return Math.ceil(minutes / 30) * 30;
  return Math.ceil(minutes / 60) * 60;
}

// Round a money amount UP to the nearest `step` (e.g. step 1000 → 5200 becomes
// 6000). step <= 0 disables rounding. Mirrors the backend `roundUpToStep`.
export function roundUpToStep(amount, step) {
  const a = Number(amount) || 0;
  const s = Math.max(0, Math.round(Number(step) || 0));
  if (s <= 0 || a <= 0) return Math.max(0, Math.round(a));
  return Math.ceil(a / s) * s;
}

export function calculateBillClient({
  startTime,
  now = Date.now(),
  pricingType = 'hourly',
  hourlyRateSnapshot = 0,
  graceMinutesSnapshot = 0,
  roundingSnapshot = 'ceil_hour',
  minimumChargeSnapshot = 0,
  packageSnapshot = null,
  fixedAmount = 0,
  openAmount = 0
} = {}) {
  if (!startTime) return { minutes: 0, total: 0 };
  const start = new Date(startTime).getTime();
  const end = typeof now === 'number' ? now : new Date(now).getTime();
  const rawMinutes = Math.max(0, Math.floor((end - start) / 60000));

  if (pricingType === 'fixed') {
    return { minutes: rawMinutes, total: Number(fixedAmount) || 0 };
  }
  if (pricingType === 'open') {
    return { minutes: rawMinutes, total: Number(openAmount) || 0 };
  }

  if (pricingType === 'package' && packageSnapshot) {
    const dur = Number(packageSnapshot.durationMinutes) || 0;
    const base = Number(packageSnapshot.price) || 0;
    if (rawMinutes <= dur) return { minutes: rawMinutes, total: base };
    const overMinutes = rawMinutes - dur;
    const extraRate = Number(packageSnapshot.extraHourPrice) || 0;
    // Prorate per-minute (mirrors backend engine) so the preview climbs gradually.
    const extraAmount = Math.round((overMinutes / 60) * extraRate);
    return { minutes: rawMinutes, total: base + extraAmount };
  }

  // hourly
  const billable = Math.max(0, rawMinutes - (Number(graceMinutesSnapshot) || 0));
  const buckets = ceilToBucket(billable, roundingSnapshot);
  const rate = Number(hourlyRateSnapshot) || 0;
  const amount = (buckets / 60) * rate;
  const total = Math.max(amount, Number(minimumChargeSnapshot) || 0);
  return { minutes: rawMinutes, total };
}
